import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Survey } from 'src/app/satisfaction/models/survey.model';
import { Member } from '../../models/member/member.model';
import { RoomBooking } from '../../models/room-booking/room-booking.model';
import { CourseInstance } from '../../models/training/course-instance.model';
import { Course } from '../../models/training/course.model';
import { DefMemberNamePipe } from '../../pipes/member-name.pipe';
import { MemberService } from '../../services/member.service';

/**
 * Used to translate the body of the popup window
 */
interface TranslationParams {
  date: string;
  link: string;
  linkName: string;
}

/**
 * Used to translate the body of the popup window when the survey topic is a course instance
 */
interface CourseInstanceTranslationParams extends TranslationParams {
  course: string;
  instructor: string;
}

/**
 * Used to translate the body of the popup window when the survey topic is a room booking
 */
interface RoomBookingTranslationParams extends TranslationParams {
  room: string;
}

@Component({
  selector: 'def-satisfaction-popup',
  templateUrl: './satisfaction-popup.component.html',
  styleUrls: ['./satisfaction-popup.component.scss'],
})
export class SatisfactionPopupComponent implements OnInit {

  currentUser: Member;
  loaded = false;

  translationParams: TranslationParams;

  constructor(
    private memberService: MemberService,
    private translateService: TranslateService,
    private router: Router,
    private toastr: ToastrService,
    private dialogRef: MatDialogRef<SatisfactionPopupComponent>,
    private memberNamePipe: DefMemberNamePipe,
    private datePipe: DatePipe,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
  }

  ngOnInit() {
    this.memberService.currentUser.subscribe(
      (currentUser: Member) => {
        this.currentUser = currentUser;
        this.loaded = true;
      });
    this.translationParams = this.getTranslationParams(this.data.object);
  }

  goToSatisfactionForm() {
    const topicId: string = this.data.object['@id'];
    const survey: Survey = this.data.survey;

    if (!survey) {
      this.toastr.info(this.translateService.instant('SATISFACTION.LIST.NOSURVEYFORTHISTOPIC'));
    } else {
      const urlCommands = ['satisfaction/answer', survey.id, topicId];
      this.router.navigate(urlCommands);
    }
  }

  onValid() {
    this.goToSatisfactionForm();
    this.onClose();
  }

  onClose(result?: any) {
    this.dialogRef.close(result);
  }

  /**
   * Returns translations params depending on the survey topic
   */
  private getTranslationParams(surveyTopic: RoomBooking | CourseInstance): TranslationParams {
    if (surveyTopic['@type'].includes('CourseInstance')) {
      return this.getInstanceTranslationParams(new CourseInstance(surveyTopic));
    } else if (surveyTopic['@type'].includes('RoomBooking')) {
      return this.getBookingTranslationParams(surveyTopic as RoomBooking);
    }
  }

  /**
   * Returns translation params based on course instance
   *
   * @param instance Course instance that is the survey topic
   */
  private getInstanceTranslationParams(instance: CourseInstance): CourseInstanceTranslationParams {
    const category = this.translateService.instant('ME.TITLE');
    const menuItem = this.translateService.instant('ME.SIDEMENU.FORMATION');

    return {
      course: (instance.course as Course).name,
      date: this.datePipe.transform(instance.scheduleActions[0].startTime, 'fullDate'),
      instructor: this.memberNamePipe.transform(instance.instructors[0], 'fullName'),
      link: this.router.createUrlTree(['me/formation']).toString(),
      linkName: `${category} > ${menuItem}`,
    };
  }

  /**
   * Returns translation params based on room booking
   *
   * @param booking Room booking that is the survey topic
   */
  private getBookingTranslationParams(booking: RoomBooking): RoomBookingTranslationParams {
    const category = this.translateService.instant('ME.TITLE');
    const menuItem = this.translateService.instant('ME.SIDEMENU.RESERVATION');

    return {
      room: booking.room.resourceName,
      date: this.datePipe.transform(booking.startDate, 'fullDate'),
      link: this.router.createUrlTree(['me/reservation']).toString(),
      linkName: `${category} > ${menuItem}`,
    };
  }
}
